import React, { useEffect, useState, useRef } from "react";
import "./OneTime.css";
import "../Auth/Login.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LoginLogo from "../../Components/Assets/images/logo2.png";
import { useTitle } from "../../hooks/useTitle";
import { ChevronLeft } from "lucide-react";
import { account, tablesDB, DB_ID, USERS_TABLE } from "../../appwrite";
import { ID } from "appwrite";
import { useStateValue } from "../../StateProvider";

function OneTime() {
  useTitle("Verify Phone Number");
  const navigate = useNavigate();
  const location = useLocation();
  const [, dispatch] = useStateValue();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userId, setUserId] = useState("");
  const [flowType, setFlowType] = useState("signup");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (location.state) {
      setPhoneNumber(location.state.phoneNumber || "");
      setUserId(location.state.userId || "");
      setFlowType(location.state.flowType || "signup");
    }
  }, [location.state]);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((p) => p - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index) => {
    if (/[^0-9]/.test(value)) return;
    setError("");
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newOtp.join("").length === 6) verifyOtp(newOtp.join(""));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = pasted.split("").concat(new Array(6).fill("")).slice(0, 6);
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) verifyOtp(pasted);
  };

  const verifyOtp = async (secret) => {
    setLoading(true);
    setError("");
    try {
      if (flowType === "signup") {
        await account.updatePhoneSession({ userId, secret });
      } else {
        await account.updatePhoneVerification({ userId, secret });
      }
      // Re-fetch user and update global state after verification
      try {
        const user = await account.get();
        try {
          const doc = await tablesDB.getRow({ databaseId: DB_ID, tableId: USERS_TABLE, rowId: user.$id });
          dispatch({ type: "SET_USER", user: { ...user, ...doc } });
        } catch {
          dispatch({ type: "SET_USER", user });
        }
      } catch {}
      navigate("/");
    } catch {
      setError("Invalid code. Please try again.");
      setOtp(new Array(6).fill(""));
      inputRefs.current[0]?.focus();
      setTimer(30);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    try {
      if (flowType === "signup") {
        await account.createPhoneSession(ID.unique(), phoneNumber);
      } else {
        await account.createPhoneVerification(userId);
      }
      setOtp(new Array(6).fill(""));
      setTimer(30);
      inputRefs.current[0]?.focus();
    } catch {
      setError("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
        <h1 className="auth-heading">Enter OTP</h1>
        <p className="auth-sub">
          {phoneNumber ? `Code sent to ${phoneNumber}` : "Enter the 6-digit code we sent you"}
        </p>

        {error && <div className="auth-error">{error}</div>}

        <div className="otp-container" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={(el) => (inputRefs.current[index] = el)}
              className="otp-box"
              autoFocus={index === 0}
              disabled={loading}
            />
          ))}
        </div>

        {loading && <p className="otp-verifying">Verifying…</p>}

        {timer > 0 ? (
          <p className="otp-timer">Resend code in <strong>{timer}s</strong></p>
        ) : (
          <button className="otp-resend-btn" onClick={handleResend} disabled={loading}>
            {loading ? "Sending…" : "Resend Code"}
          </button>
        )}

        <Link to={flowType === "signup" ? "/signup" : "/login"} className="otp-back">
          <ChevronLeft size={15} /> Go back
        </Link>
      </div>
    </div>
  );
}

export default OneTime;
